(() => {
  'use strict';
  const root = document.getElementById('tanwa-subscription');
  if (!root || root.dataset.twBound === 'true') return;
  root.dataset.twBound = 'true';
  const dialog = root.querySelector('[data-tw-dialog]');
  const form = root.querySelector('[data-tw-form]');
  const email = root.querySelector('[data-tw-email]');
  const submit = root.querySelector('[data-tw-submit]');
  const error = root.querySelector('[data-tw-error]');
  const signup = root.querySelector('[data-tw-signup]');
  const success = root.querySelector('[data-tw-success]');
  if (!dialog || !form || !email || !submit || !signup || !success) return;
  const prefix = 'tanwa.subscription.v1.';
  const keys = { session: prefix + 'session', dismissed: prefix + 'dismissed', subscribed: prefix + 'subscribed', pending: prefix + 'pending', nativePending: prefix + 'native-pending', lock: prefix + 'lock' };
  const number = (name, fallback) => Number(root.dataset[name]) > 0 ? Number(root.dataset[name]) : fallback;
  const delay = number('delay', 15) * 1000;
  const scrollRequired = number('scroll', 40) / 100;
  const idle = number('idle', 30) * 60000;
  const dismissDuration = number('dismissDays', 7) * 86400000;
  const designMode = root.dataset.designMode === 'true';
  const enabled = root.dataset.enabled === 'true';
  const eligible = root.dataset.eligible === 'true';
  const uid = () => globalThis.crypto?.randomUUID?.() || Date.now().toString(36) + Math.random().toString(36).slice(2);
  const tab = uid();
  const memory = new Map();
  const getStorage = name => { try { return window[name]; } catch (_) { return null; } };
  const local = getStorage('localStorage');
  const perTab = getStorage('sessionStorage');
  const read = (key, area = local) => {
    try { return JSON.parse(area.getItem(key)); } catch (_) { return memory.get(key) ?? null; }
  };
  const write = (key, value, area = local) => {
    memory.set(key, value);
    try { area.setItem(key, JSON.stringify(value)); } catch (_) {}
  };
  const remove = (key, area = local) => {
    memory.delete(key);
    try { area.removeItem(key); } catch (_) {}
  };
  let storageAvailable = false;
  try { local.setItem(prefix + 'test', '1'); local.removeItem(prefix + 'test'); storageAvailable = true; } catch (_) {}
  const pause = ms => new Promise(resolve => setTimeout(resolve, ms));
  const locked = async callback => {
    if (globalThis.navigator.locks?.request) return navigator.locks.request(prefix + 'state', callback);
    if (!storageAvailable) return callback();
    const lease = { owner: tab + uid(), until: Date.now() + 1800 };
    for (let attempt = 0; attempt < 15; attempt++) {
      const current = read(keys.lock);
      if (current && current.until > Date.now()) { await pause(40 + Math.random() * 40); continue; }
      write(keys.lock, lease);
      await pause(65);
      if (read(keys.lock)?.owner !== lease.owner) continue;
      try { return callback(); } finally { if (read(keys.lock)?.owner === lease.owner) remove(keys.lock); }
    }
    return null;
  };
  const pageKey = (() => {
    let hash = 2166136261;
    for (const character of location.pathname) hash = Math.imul(hash ^ character.charCodeAt(0), 16777619);
    return (hash >>> 0).toString(36);
  })();
  const session = (now, renew = true) => {
    let state = read(keys.session);
    if (!state || !Number.isFinite(state.lastActivity) || (renew && now - state.lastActivity >= idle)) {
      state = { id: uid(), lastActivity: now, activeMs: 0, activeThrough: now, pages: [], scrolled: false, impressed: false };
    }
    state.activeMs = Number.isFinite(state.activeMs) ? state.activeMs : 0;
    state.pages = Array.isArray(state.pages) ? state.pages.slice(0, 2) : [];
    state.scrolled = state.scrolled === true;
    return state;
  };
  const foreground = () => document.visibilityState !== 'hidden' && document.hasFocus();
  const visible = (element, includeOwn = false) => {
    if (!element || (!includeOwn && root.contains(element)) || element.closest('[hidden], [aria-hidden="true"]')) return false;
    const details = element.closest('details');
    if (details && !details.open) return false;
    const style = getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || Number(style.opacity) === 0) return false;
    for (let parent = element.parentElement; parent; parent = parent.parentElement) if (Number(getComputedStyle(parent).opacity) === 0) return false;
    return Array.from(element.getClientRects()).some(rect => rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth);
  };
  const challengeFrame = () => Array.from(document.querySelectorAll('iframe[title*="hcaptcha challenge" i], iframe[src*="hcaptcha" i][src*="frame=challenge" i]')).find(element => visible(element, true));
  const blocked = () => {
    const selectors = 'dialog[open], [role="dialog"], [role="alertdialog"], [aria-modal="true"], #shopify-pc__banner, #shopify-pc__prefs, .shopify-pc__prefs__overlay, cart-drawer.active, #cart-notification.active, details-modal details[open], .menu-drawer-container[open], modal-dialog[open]';
    return Boolean(challengeFrame()) || Array.from(document.querySelectorAll(selectors)).some(element => visible(element));
  };
  const suppressed = () => root.dataset.subscriber === 'true' || read(keys.subscribed) === true || Number(read(keys.dismissed)) > Date.now();
  const track = (action, surface, reason) => {
    const detail = { action, surface };
    if (reason) detail.reason = reason;
    document.dispatchEvent(new CustomEvent('tanwa:subscription', { detail }));
  };
  let restoreFocus = null;
  let ownClosing = false;
  let shown = false;
  let successActive = false;
  let ownOutcome = false;
  let suspended = null;
  let response = null;
  let submitting = false;
  let clientInvalid = email.getAttribute('aria-invalid') === 'true';
  let tickBusy = false;
  let lastTick = Date.now();
  let registeredSession = null;
  let scrolled = false;
  const focusable = () => Array.from(dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter(element => !element.closest('[hidden]') && element.getClientRects().length);
  const closeUI = (restore = true) => {
    if (dialog.open) { ownClosing = true; dialog.close(); }
    document.documentElement.classList.remove('tw-subscription-open');
    shown = false;
    suspended = null;
    if (restore && restoreFocus?.isConnected) restoreFocus.focus({ preventScroll: true });
  };
  const dismiss = reason => {
    if (!designMode) write(keys.dismissed, Date.now() + dismissDuration);
    closeUI();
    track('close', 'modal', reason);
    response = null;
  };
  const openModal = (state = 'signup', preserveFocus = false) => {
    if (blocked() || !foreground()) return false;
    if (!dialog.open && !preserveFocus) restoreFocus = document.activeElement;
    signup.hidden = state === 'success';
    success.hidden = state !== 'success';
    successActive = state === 'success';
    dialog.setAttribute('aria-labelledby', state === 'success' ? 'TanwaSubscriptionSuccessTitle' : 'TanwaSubscriptionTitle');
    if (state === 'success') {
      const title = success.querySelector('h2');
      if (title) title.id = 'TanwaSubscriptionSuccessTitle';
    }
    try { if (!dialog.open) dialog.showModal(); } catch (_) { return false; }
    document.documentElement.classList.add('tw-subscription-open');
    shown = true;
    const target = state === 'success' ? success : email;
    requestAnimationFrame(() => target.focus({ preventScroll: true }));
    track('open', 'modal', state === 'success' ? 'success' : 'signup');
    return true;
  };
  const show = () => {
    if (!openModal()) return false;
    track('impression', 'modal');
    return true;
  };
  const updateScroll = () => {
    const height = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight) - innerHeight;
    if (height > 0 && Math.max(scrollY, document.documentElement.scrollTop) / height >= scrollRequired) scrolled = true;
  };
  const suspendModal = () => {
    suspended = 'modal';
    ownClosing = true;
    dialog.close();
    document.documentElement.classList.remove('tw-subscription-open');
  };
  const tick = async () => {
    if (tickBusy) return;
    tickBusy = true;
    const now = Date.now();
    const deltaFrom = lastTick;
    lastTick = now;
    try {
      if (response) {
        if (!blocked() && foreground() && openModal(response)) {
          track(response === 'success' ? 'success' : 'error', 'modal', 'server');
          response = null;
        }
        return;
      }
      if (shown && suppressed() && !successActive) { closeUI(); return; }
      if (shown && blocked()) {
        if (dialog.open) {
          suspendModal();
        }
        return;
      }
      if (shown && suspended) {
        if (openModal(successActive ? 'success' : 'signup', true)) suspended = null;
      }
      if (!enabled || designMode || suppressed() || !foreground()) return;
      updateScroll();
      await locked(() => {
        const state = session(now, false);
        if (now - state.lastActivity >= idle) return;
        if (eligible && registeredSession !== state.id) {
          if (!state.pages.includes(pageKey) && state.pages.length < 2) state.pages.push(pageKey);
          registeredSession = state.id;
        }
        const from = Math.max(deltaFrom, state.activeThrough || now, now - 1000);
        state.activeMs = Math.min(delay, state.activeMs + Math.max(0, now - from));
        state.activeThrough = now;
        if (eligible && scrolled) state.scrolled = true;
        if (eligible && !shown && !state.impressed && state.activeMs >= delay && (state.scrolled || state.pages.length >= 2) && !blocked() && !suppressed()) {
          if (show()) { state.impressed = true; state.impressionOwner = tab; }
        }
        write(keys.session, state);
      });
    } finally { tickBusy = false; }
  };
  const activity = async () => {
    if (designMode || !enabled || !foreground()) return;
    const now = Date.now();
    await locked(() => {
      const previous = read(keys.session);
      const state = session(now);
      if (previous?.id !== state.id) {
        registeredSession = null;
        scrolled = false;
        lastTick = now;
        if (shown && !ownOutcome && !submitting) closeUI(false);
      }
      state.lastActivity = now;
      if (eligible && scrolled) state.scrolled = true;
      if (eligible && registeredSession !== state.id) {
        if (!state.pages.includes(pageKey) && state.pages.length < 2) state.pages.push(pageKey);
        registeredSession = state.id;
      }
      write(keys.session, state);
    });
  };
  root.querySelectorAll('[data-tw-close]').forEach(button => button.addEventListener('click', () => dismiss('button')));
  dialog.addEventListener('cancel', event => { event.preventDefault(); dismiss('escape'); });
  dialog.addEventListener('close', () => { if (ownClosing) { ownClosing = false; return; } if (shown) dismiss('close'); });
  let backdropDown = false;
  dialog.addEventListener('pointerdown', event => { backdropDown = event.target === dialog; });
  dialog.addEventListener('click', event => {
    if (event.target !== dialog || !backdropDown) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dismiss('backdrop');
  });
  dialog.addEventListener('keydown', event => {
    if (event.key === 'Escape') { event.preventDefault(); dismiss('escape'); return; }
    if (event.key !== 'Tab') return;
    const items = focusable();
    if (!items.length) { event.preventDefault(); dialog.focus(); return; }
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || !items.includes(document.activeElement))) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && (document.activeElement === last || !items.includes(document.activeElement))) { event.preventDefault(); first.focus(); }
  });
  const invalid = () => {
    clientInvalid = true;
    email.setAttribute('aria-invalid', 'true');
    if (error) { error.textContent = error.dataset.validationMessage || ''; error.hidden = false; }
    email.focus({ preventScroll: true });
  };
  email.addEventListener('invalid', event => { event.preventDefault(); invalid(); });
  email.addEventListener('input', () => {
    if (!clientInvalid) return;
    email.removeAttribute('aria-invalid');
    if (error) error.hidden = true;
    clientInvalid = false;
  });
  const stagePopup = () => {
    if (designMode || submitting || !email.validity.valid) return;
    remove(keys.nativePending, perTab);
    write(keys.pending, { id: form.id, nonce: uid(), at: Date.now(), hash: '#' + form.id }, perTab);
  };
  const stageNative = nativeForm => {
    if (designMode || nativeForm === form || !(nativeForm instanceof HTMLFormElement) || !nativeForm.id) return;
    const nativeEmail = nativeForm.querySelector('[name="contact[email]"]');
    if (nativeForm.querySelector('[name="form_type"]')?.value !== 'customer' || !nativeEmail?.validity.valid) return;
    try {
      const endpoint = new URL(nativeForm.action, location.href);
      if (endpoint.origin !== location.origin) return;
      const returnValue = nativeForm.querySelector('[name="return_to"]')?.value;
      const hash = returnValue ? new URL(returnValue, location.href).hash : endpoint.hash;
      if (hash !== '#' + nativeForm.id) return;
      remove(keys.pending, perTab);
      write(keys.nativePending, { id: nativeForm.id, nonce: uid(), at: Date.now(), hash }, perTab);
    } catch (_) {}
  };
  window.addEventListener('click', event => {
    if (designMode || !event.isTrusted || !(event.target instanceof Element)) return;
    const control = event.target.closest('button, input[type="submit"], input[type="image"]');
    if (!control || !['submit', 'image'].includes(control.type)) return;
    if (control === submit) {
      if (submitting) { event.preventDefault(); return; }
      stagePopup();
    } else stageNative(control.form);
  }, true);
  window.addEventListener('keydown', event => {
    if (designMode || !event.isTrusted || event.key !== 'Enter' || !(event.target instanceof HTMLInputElement)) return;
    if (event.target.form === form) stagePopup();
    else stageNative(event.target.form);
  }, true);
  submit.addEventListener('click', event => { if (submitting && event.isTrusted) event.preventDefault(); });
  form.addEventListener('keydown', event => { if (submitting && event.key === 'Enter' && event.isTrusted) event.preventDefault(); });
  form.addEventListener('submit', event => {
    if (designMode) { event.preventDefault(); return; }
    if (!email.checkValidity()) { event.preventDefault(); invalid(); return; }
    if (submitting) return;
    submitting = true;
    remove(keys.nativePending, perTab);
    write(keys.pending, { id: form.id, nonce: uid(), at: Date.now(), hash: '#' + form.id }, perTab);
    form.setAttribute('aria-busy', 'true');
    submit.setAttribute('aria-disabled', 'true');
    track('submit', 'modal');
    setTimeout(() => {
      submitting = false;
      form.removeAttribute('aria-busy');
      submit.removeAttribute('aria-disabled');
    }, 60000);
  }, true);
  const pending = read(keys.pending, perTab);
  const formState = form.dataset.formState || form.querySelector('[data-tw-form-state]')?.dataset.twFormState || 'idle';
  if (pending && Date.now() - pending.at >= 600000) remove(keys.pending, perTab);
  if (enabled && !designMode && pending?.id === form.id && pending.nonce && Date.now() - pending.at < 600000 && location.hash === pending.hash && ['success', 'errors'].includes(formState)) {
    ownOutcome = true;
    response = formState === 'success' ? 'success' : 'signup';
    if (formState === 'success') write(keys.subscribed, true);
    remove(keys.pending, perTab);
  }
  const nativePending = read(keys.nativePending, perTab);
  if (!designMode && nativePending?.nonce && nativePending.id !== form.id && Date.now() - nativePending.at < 600000 && nativePending.hash === location.hash && formState === 'success') {
    write(keys.subscribed, true);
    remove(keys.nativePending, perTab);
  } else if (nativePending && Date.now() - nativePending.at >= 600000) remove(keys.nativePending, perTab);
  document.addEventListener('submit', event => {
    stageNative(event.target);
  }, true);
  if (root.dataset.subscriber === 'true' && !designMode) write(keys.subscribed, true);
  document.addEventListener('visibilitychange', () => { lastTick = Date.now(); if (foreground()) activity(); });
  window.addEventListener('focus', () => { lastTick = Date.now(); activity(); });
  window.addEventListener('blur', () => { lastTick = Date.now(); });
  window.addEventListener('pageshow', () => { submitting = false; form.removeAttribute('aria-busy'); submit.removeAttribute('aria-disabled'); lastTick = Date.now(); activity(); });
  window.addEventListener('pagehide', () => { lastTick = Date.now(); });
  window.addEventListener('scroll', () => { updateScroll(); activity(); }, { passive: true });
  document.addEventListener('pointerdown', activity, { passive: true });
  document.addEventListener('keydown', activity, { passive: true });
  window.addEventListener('storage', event => {
    if (event.key === keys.subscribed || event.key === keys.dismissed) { if (suppressed() && !successActive) closeUI(); }
    if (event.key === keys.session && shown && !response && !ownOutcome && read(keys.session)?.impressionOwner !== tab) closeUI();
  });
  if (designMode) document.addEventListener('shopify:section:select', event => { if (event.target.contains(root)) openModal(); });
  if (!enabled) return;
  new MutationObserver(() => {
    if (shown && dialog.open && challengeFrame()) suspendModal();
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden', 'title', 'src'] });
  activity().then(tick);
  setInterval(tick, 250);
})();
